import { Button, Icon, Modal } from 'lmc-ui-library';
import { Box, Columns } from 'modules/af_cm_ui_kit';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { CaseStatuses } from 'modules/cm/domain/Case/CaseStatuses';
import { StoreReadonlyField } from 'modules/cm/fields/EditableField';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import fields, { EditedField, FIELDS, INPUTS } from 'modules/cm/fields/types';
import useCaseStatus from 'modules/cm/hooks/useCaseStatus';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import { sections } from 'modules/cm/pages/CaseDetailsPage/partials/';
import { saveCase } from 'modules/cm/store/cases/actions';
import { changeCaseStatus } from 'modules/cm/store/useCases';
import { ChangeCaseStatusError } from 'modules/cm/store/useCases/changeCaseStatus';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Nullable } from 'utils/types';
import useYupValidationResolver from '../../../../../../form/yup/reactHookFormResolver';
import CheckDirty from '../../../../components/CheckDirty/CheckDirty';
import validationSchema from '../../../../form/Case/LoanDetails/validationSchema';
import { updateLoanDetails } from '../../../../store/cases/update/actions';
import * as subPages from '../../../../store/subPages';
import { SUBPAGES } from '../../../../store/subPages/types';
import useIsCaseStatue from '../../hooks/useIsCaseStatus';
import useSaveButtonClicked from '../../hooks/useSaveButtonClicked';
import { SubPageTemplate } from '../../partials';
import { ErrorItem, ErrorText } from '../Credit/CreditComponent.styled';
import { FormModel, Props } from './Summary.types';

const Summary: React.FC<Props> = ({
  modulePrefix,
  data,
  initialData,
  resources,
  caseId,
  onStatusChange,
  onProductChange,
  onActionDone,
}) => {
  const dispatch = useDispatch();
  const { addListener, removeListener } = useSaveButtonClicked();

  const methods = useForm<FormModel>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { watch, reset } = methods;
  const [switchCaseStatusError, setSwitchCaseStatusError] = useState<string>('');
  const currentCaseStatus = useCaseStatus();
  const isUpploadPepper = useIsCaseStatue(CaseStatuses.Completed);
  const { getErrors } = usePepperError('loanDetails');
  const watchCaseStatusValue: Nullable<string> = watch(FIELDS.STATUS);

  useEffect(() => {
    reset(data, {
      keepDefaultValues: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateLoanDetails(methods.getValues()));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateLoanDetails(methods.getValues()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data[FIELDS.STATUS] === watchCaseStatusValue) return;

    handleCaseStatusChange({
      field: fields[FIELDS.STATUS],
      value: watchCaseStatusValue,
      fullPath: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchCaseStatusValue]);

  const handleCaseStatusChange = async (editedField: EditedField) => {
    try {
      const upcomingStatus = editedField.value;
      const actionsDone = await dispatch<any>(changeCaseStatus(caseId, currentCaseStatus, upcomingStatus));

      if (actionsDone.includes('HARD_FOOTPRINT')) {
        onActionDone(`The applicant's credit file was successfully updated with a hard footprint.`);
      }

      onStatusChange();
    } catch (e) {
      if (e instanceof ChangeCaseStatusError) {
        setSwitchCaseStatusError(e.message);
      } else {
        setSwitchCaseStatusError(
          'Sorry, your request to change the case status could not be completed. Please try again.',
        );
      }
    }
  };

  return (
    <SubPageTemplate title="Loan details">
      <FormProvider<FormModel> {...methods}>
        <CheckDirty
          extraBlacklistedFields={[FIELDS.STATUS]}
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(subPages.actions.updateSubPageIsDirty(SUBPAGES.LOAN_DETAILS, isDirty));
          }}
        />
        <form onSubmit={() => {}}>
          <Columns spacing={4}>
            <>
              <sections.Underwriting
                modulePrefix={modulePrefix}
                resources={resources}
                isStatusCompleted={isUpploadPepper}
                caseStatusChange={handleCaseStatusChange}
              />
              <sections.Application modulePrefix={modulePrefix} resources={resources} />

              <Box title="Affordability">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.AFT_PREFIX]()[FIELDS.AFT_STAGE]}
                  variant={VARIANTS_TYPES.TEXT}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.AFT_PREFIX]()[FIELDS.AFT_INCOME]}
                  variant={VARIANTS_TYPES.DECIMAL_CURRENCY}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.AFT_PREFIX]()[FIELDS.AFT_BUFFER]}
                  variant={VARIANTS_TYPES.DECIMAL_CURRENCY}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.AFT_PREFIX]()[FIELDS.AFT_LIVING_COSTS]}
                  variant={VARIANTS_TYPES.DECIMAL_CURRENCY}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.AFT_PREFIX]()[FIELDS.AFT_NET_CASH_FLOW]}
                  variant={VARIANTS_TYPES.DECIMAL_CURRENCY}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.AFT_PREFIX]()[FIELDS.AFT_MAX_LOAN_POSSIBLE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
              </Box>
              <Box title="Bank Details">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ACCOUNT_NAME]}
                  getErrors={getErrors}
                />

                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SORT_CODE]}
                  variant={VARIANTS_TYPES.SORT_CODE}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ACCOUNT_NUMBER]}
                  variant={VARIANTS_TYPES.BANK_ACC_NUMBER}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.BANK_OR_BUILDING_SOCIETY_NAME]}
                />
              </Box>
              <Box title="Additional information">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXTAREA}
                  field={fields[FIELDS.ADDITIONAL_DETAILS]}
                  variant={VARIANTS_TYPES.TEXT}
                />
              </Box>
            </>
            <>
              <Box title="Loan">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PURPOSE_OF_THE_LOAN]}
                  options={resources.purposesOfTheLoan}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PURPOSE_OF_CAPITAL_RAISE]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.PEPPER_PURPOSE_OF_THE_LOAN]}
                  options={resources.pepperPurposesOfTheLoan}
                  searchable={true}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.FCA_LOAN_PURPOSE]}
                  options={resources.fcaLoanPurposes}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_AMOUNT_PURCHASE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_AMOUNT_REMORTGAGE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_DEBT_CONSOLIDATION]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_HOME_IMPROVEMENTS]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_DISCRETIONARY_SPEND]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_ASSISTING_OTHERS]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.LOAN_OTHER_PURPOSES]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.NET_LOAN]}
                  variant={VARIANTS_TYPES.CURRENCY}
                  isUpdateable={true}
                />
                <StoreReadonlyField field={fields[FIELDS.GROSS_LOAN]} variant={VARIANTS_TYPES.CURRENCY} />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ESTIMATED_PROPERTY_VALUE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.PROPERTY_PURCHASE_PRICE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.ACTUAL_PROPERTY_VALUE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <StoreReadonlyField field={fields[FIELDS.LTV]} variant={VARIANTS_TYPES.DECIMAL_PERCENTAGE} />
                <StoreReadonlyField field={fields[FIELDS.VALUATION_FEE_AMOUNT]} variant={VARIANTS_TYPES.CURRENCY} />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.VALUATION_FEE_PAYMENT]}
                  options={resources.valuationFees}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.RE_INSPECTION_FEE]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.CURRENT_MORTGAGE_PROVIDER]}
                  getErrors={getErrors}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.OUTSTANDING_MORTGAGE_AMOUNT]}
                  variant={VARIANTS_TYPES.CURRENCY}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.SOURCE_OF_FUNDS_TO_CLEAR_OUTSTANDING_MORTGAGE]}
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.SELECT}
                  field={fields[FIELDS.EXISTING_MORTGAGE_STATUS]}
                  options={resources.existingMortgageStatuses}
                  required
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.TEXT}
                  field={fields[FIELDS.POSTCODE]}
                  variant={VARIANTS_TYPES.POSTCODE}
                />
              </Box>

              <sections.Product
                caseId={caseId}
                resources={resources}
                onProductChange={onProductChange}
                getErrors={getErrors}
              />

              <Box title="Marketing">
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  field={fields[FIELDS.HAS_MARKETING_EMAIL]}
                  variant="inline"
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  field={fields[FIELDS.HAS_MARKETING_SMS]}
                  variant="inline"
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  field={fields[FIELDS.HAS_MARKETING_PHONE]}
                  variant="inline"
                />
                <UpdatableField
                  modulePrefix={modulePrefix}
                  getErrors={getErrors}
                  fieldType={INPUTS.CHECKBOX}
                  field={fields[FIELDS.HAS_MARKETING_POST]}
                  variant="inline"
                />
              </Box>
            </>
          </Columns>
        </form>
      </FormProvider>

      <Modal showCloseBtn={false} isOpen={!!switchCaseStatusError}>
        <Modal.Header title="Error!" mode="error" />
        <Modal.Content>
          <ErrorItem>
            <Icon type="warning" color="#FA5245" />
            <ErrorText>{switchCaseStatusError}</ErrorText>
          </ErrorItem>
        </Modal.Content>
        <Modal.Footer style={{ display: 'grid' }}>
          <Button mode="ghost" borderVariant="rectangle" onClick={setSwitchCaseStatusError.bind(null, '')}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </SubPageTemplate>
  );
};

export default Summary;
